import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from '@core/components/base-component';
import { DepartmentEstimateHelper } from '@core/helpers/department-estimate.helper';
import { StatusEnum } from '@enums/Status';
import { Estimate } from '@interfaces/Estimate';
import { EstimateLine } from '@interfaces/EstimateLine';
import { Structure } from '@interfaces/Structure';
import { TreeCellDropdownService } from '@shared/components/tree-cell-dropdown/tree-cell-dropdown.service';
import { EstimateBudgetService } from '@shared/services/estimate-budget.service';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { EstimateStructureLinksService } from '@shared/services/estimate-structure-links.service';
import { EstimateService } from '@shared/services/estimate.service';
import { OverBudgetResult } from '@shared/services/models/over-budget-result';
import { TotalsDataService } from '@shared/services/total.service';
import { TreeNode } from 'primeng/api';
import { debounceTime } from 'rxjs/operators';
import { ErrorDialogComponent } from '../../error-dialog/error-dialog.component';
import { TreeNodeData, TreeNodeType } from '../models/tree-node-data.model';

@Component({
  selector: 'pop-estimate-tree-cell',
  templateUrl: './estimate-tree-cell.component.html',
  styleUrls: ['./estimate-tree-cell.component.less']
})
export class EstimateTreeCellComponent extends BaseComponent implements OnInit {
  public isDepartment: boolean;
  public isLevel2: boolean;
  public isLoading: boolean;
  @Input() public isLineBusy = false; //there are currently editions being saved on this line.
  public isAdding: boolean;
  public isReadonly: boolean;

  @Input()
  public rowNode: {
    level?: number;
    visible?: boolean;
    node: TreeNode<TreeNodeData<Structure | Estimate | EstimateLine>>;
    parent: TreeNode<TreeNodeData<Structure | Estimate | EstimateLine>>;
  };

  @Input()
  public currentEstimate: Estimate;

  public label: string;
  public needToVerifyBudget: boolean;
  public overBudgetErrors: OverBudgetResult[] = [];
  private groupDepartments: boolean;

  public constructor(
    private totalsData: TotalsDataService,
    private estimateService: EstimateService,
    private estimateBudgetService: EstimateBudgetService,
    private estimateSettingsService: EstimateSettingsService,
    private estimateStructureLinksService: EstimateStructureLinksService,
    private treeCellDropdownService: TreeCellDropdownService,
    private dialog: MatDialog
  ) {
    super();
  }

  public async ngOnInit() {
    this.label = this.rowNode.node.data.label;
    this.isDepartment = this.rowNode.node.data.type === TreeNodeType.Department;
    this.isLevel2 = this.rowNode.node.data.type === TreeNodeType.Level2;
    this.isReadonly = this.currentEstimate.status !== StatusEnum.New;

    const settings = await this.estimateSettingsService.getEstimateConfigurationAsync();
    this.groupDepartments = settings.groupBudgetsByProjDept;

    await this.refeshIsOverBudget();
    this.subscribe(
      this.totalsData
        .observeChanges()
        .pipe(debounceTime(500))
        .subscribe(() => {
          this.refeshIsOverBudget();
        })
    );
  }

  private async refeshIsOverBudget() {
    this.overBudgetErrors = await this.estimateBudgetService.verifyIsOverBudget(
      this.currentEstimate,
      this.rowNode.node,
      this.groupDepartments
    );
  }

  public showOverBudgetModal() {
    this.dialog.open(ErrorDialogComponent, {
      data: {
        overBudgetErrors: this.overBudgetErrors,
        budgetFromGP: !this.estimateBudgetService.hasApprovedEstimate || this.currentEstimate.forceBudgetValidationGp
      }
    });
  }

  public async addDepartment(event: MouseEvent) {
    this.isAdding = true;
    const x = event.pageX;
    const y = event.pageY;

    const structure = this.rowNode.node.data.entity as Structure;

    const linkedDepartmentCodes = this.currentEstimate.departmentEstimates.map(de => de.department?.code);
    const alreadyPresentCodes = structure.estimateLines.map(line => line.departmentEstimate.department.code);

    const filteredDepartmentCodes = linkedDepartmentCodes.filter(code => !alreadyPresentCodes.includes(code));

    const department = await this.treeCellDropdownService
      .getDepartment({
        x,
        y,
        filteredDepartmentCodes
      })
      .finally(() => {
        this.isAdding = false;
      });

    if (!department) {
      // undefined if dialog is cancelled
      return;
    }

    try {
      this.isLoading = true;

      let deptEstim = this.currentEstimate.departmentEstimates.find(de => de.department.code === department.code);

      if (!deptEstim) {
        deptEstim = this.estimateStructureLinksService.linkDeptToEstimate(this.currentEstimate, department.id);
      }

      await this.estimateStructureLinksService.associateDeptEstimToStructure(
        this.currentEstimate,
        structure,
        deptEstim.id
      );

      DepartmentEstimateHelper.SetIsAssociatedToAllStructures(deptEstim, this.currentEstimate);

      await this.estimateService.flushTreeTable();
    } catch (error) {
      this.isLoading = false;
      throw error;
    }
  }

  public async removeDepartment() {
    if (this.isLineBusy) {
      return;
    }

    this.rowNode.visible = false; //avoids user editing the line while it is being deleted.

    const parentStructure = this.rowNode.parent.data.entity as Structure;
    const estimateLine = this.rowNode.node.data.entity as EstimateLine;

    await this.estimateStructureLinksService.disassociateDeptEstimFromStructure(
      parentStructure,
      estimateLine.departmentEstimateId
    );

    await this.estimateService.detachEntity(estimateLine);
  }
}
